// Registry of contract multipliers for different instruments / asset classes.
// - Returns multiplier (numeric) to be applied to PnL calculations for contracts like futures and (optionally) CASH/forex.
// - Default multiplier is 1 (no scaling). For CASH/forex, the default is 1 but you can add symbol-specific multipliers
//   when your imported `qty` values represent lots instead of base-currency units.

function parseForexPair(symbol?: string): { base: string; quote: string } | null {
  if (!symbol) return null;
  const s = String(symbol).toUpperCase().trim();
  // allow "EUR/USD", "EUR.USD" or "EURUSD"
  const slashMatch = s.match(/^([A-Z]{3})[/.]([A-Z]{3})$/);
  if (slashMatch) return { base: slashMatch[1], quote: slashMatch[2] };
  const six = s.match(/^([A-Z]{3})([A-Z]{3})$/);
  if (six) return { base: six[1], quote: six[2] };
  return null;
}

export function getForexQuoteCurrency(symbol?: string): string | undefined {
  const p = parseForexPair(symbol);
  return p ? p.quote : undefined;
}

const FUTURES_MONTH_CODES = new Set(['F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z']);

export function extractFuturesRoot(symbol: string | undefined): string {
  if (!symbol) return '';
  const normalizedRaw = String(symbol).toUpperCase().trim();
  const venueStripped = normalizedRaw.includes(':')
    ? (normalizedRaw.split(':').pop() ?? normalizedRaw)
    : normalizedRaw;
  const normalized = venueStripped.replace(/^\/+/, '');
  const token = normalized.match(/^([A-Z0-9]+)/)?.[1] ?? '';
  if (!token) return '';

  for (let i = 1; i < token.length; i++) {
    const maybeMonthCode = token[i];
    const suffix = token.slice(i + 1);
    if (FUTURES_MONTH_CODES.has(maybeMonthCode) && /^\d{1,4}$/.test(suffix)) {
      return token.slice(0, i);
    }
  }

  return token;
}

export const CME_FUTURES_MULTIPLIERS: Record<string, number> = {
  M6E: 12500,
  M6B: 6250,
  M6A: 10000,
  M6C: 10000,
  M6J: 6250000,
  M6S: 12500,
  M6N: 10000,
  '6B': 62500,
  '6A': 100000,
  '6C': 100000,
  '6J': 12500000,
  '6S': 125000,
  '6N': 100000,
  '6H': 500000,
  ES: 50,
  MES: 5,
  NQ: 20,
  MNQ: 2,
  YM: 5,
  MYM: 0.5,
  RTY: 50,
  M2K: 5,
  CL: 1000,
  MCL: 100,
  GC: 100,
  MGC: 10,
  '1OZ': 1,
  SI: 5000,
  ZC: 5000,
  '6E': 125000,
};

// Exchange-standard minimum price increments used for futures tick calculations.
// Keep this conservative and explicit: unknown roots should remain unresolved.
export const CME_FUTURES_TICK_SIZES: Record<string, number> = {
  ES: 0.25,
  MES: 0.25,
  NQ: 0.25,
  MNQ: 0.25,
  YM: 1,
  MYM: 1,
  RTY: 0.1,
  M2K: 0.1,
  CL: 0.01,
  MCL: 0.01,
  GC: 0.1,
  MGC: 0.1,
  SI: 0.005,
  HG: 0.0005,
  NG: 0.001,
  ZC: 0.25,
  ZB: 0.03125,
  ZN: 0.015625,
  ZF: 0.0078125,
  ZT: 0.00390625,
  '6E': 0.00005,
  M6E: 0.0001,
  '6B': 0.0001,
  M6B: 0.0001,
  '6A': 0.0001,
  M6A: 0.0001,
  '6C': 0.0001,
  M6C: 0.0001,
  '6N': 0.0001,
  M6N: 0.0001,
  '6S': 0.0001,
  M6S: 0.0001,
  '6J': 0.0000005,
  M6J: 0.000001,
};

function getInstrumentTickSize(instrument?: unknown): number | null {
  if (!instrument || typeof instrument !== 'object') return null;
  const candidate = Number(
    (instrument as any).tickSize
    ?? (instrument as any).minTick
    ?? (instrument as any).priceIncrement
    ?? (instrument as any).minimumPriceIncrement
  );
  if (!Number.isFinite(candidate) || candidate <= 0) return null;
  return candidate;
}

export function resolveFuturesTickSize(symbol?: string, instrument?: unknown): number | null {
  const fromInstrument = getInstrumentTickSize(instrument);
  if (fromInstrument != null) return fromInstrument;

  const root = extractFuturesRoot(symbol);
  const mapped = CME_FUTURES_TICK_SIZES[root];
  if (!Number.isFinite(mapped) || mapped <= 0) return null;
  return mapped;
}

export function computeDirectionalTicks(
  entryPrice: number,
  exitPrice: number,
  side: 'buy' | 'sell',
  tickSize: number,
): number | null {
  if (!Number.isFinite(entryPrice) || !Number.isFinite(exitPrice)) return null;
  if (!Number.isFinite(tickSize) || tickSize <= 0) return null;

  const signedPriceMove = side === 'sell'
    ? (entryPrice - exitPrice)
    : (exitPrice - entryPrice);
  const ticks = signedPriceMove / tickSize;
  if (!Number.isFinite(ticks)) return null;
  return ticks;
}

// ============================================================================
// Tick Size Resolution Diagnostics
// ============================================================================

/** Structured diagnostic result explaining how tick size was (or wasn't) resolved. */
export interface TickSizeResolutionDiagnostic {
  /** Asset class inferred from symbol or metadata */
  assetClass: 'futures' | 'spot' | 'unknown';
  /** Original symbol as provided */
  originalSymbol: string;
  /** Normalized root symbol used for fallback lookup (e.g. 'ES' from 'ESZ25') */
  normalizedSymbol: string | null;
  /** Whether the symbol is treated as futures */
  isFutures: boolean;
  /** Resolution source: metadata > fallback map > unresolved */
  resolutionSource: 'instrument-metadata' | 'fallback-root-map' | 'unresolved';
  /** Resolved tick size, or null if unresolved */
  resolvedTickSize: number | null;
  /** Reason code explaining why resolution failed (only set if unresolved) */
  unresolvedReason: string | null;
}

/**
 * Determine asset class heuristically from symbol and metadata.
 * - If assetClass='FUTURES' is explicit, return 'futures'
 * - If symbol matches futures root pattern, return 'futures'
 * - Otherwise return 'spot' or 'unknown'
 */
function inferAssetClass(symbol?: string, assetClass?: string | null): 'futures' | 'spot' | 'unknown' {
  if ((assetClass || '').toUpperCase() === 'FUTURES') return 'futures';
  if ((assetClass || '').toUpperCase() === 'CASH') return 'spot';

  if (symbol) {
    const root = extractFuturesRoot(symbol);
    if (root && CME_FUTURES_TICK_SIZES[root]) return 'futures';
    if (root && CME_FUTURES_MULTIPLIERS[root]) return 'futures';
  }

  return 'unknown';
}

/**
 * Get a structured diagnostic explaining exactly how tick size was resolved (or why it failed).
 * Use this to debug blank P&L (Ticks) values or verify resolution occurred as expected.
 *
 * @param symbol - The symbol (e.g. 'ESZ25', 'AAPL', 'EUR/USD')
 * @param assetClass - Optional asset class hint (e.g. 'FUTURES', 'CASH')
 * @param instrument - Optional instrument metadata with tickSize, minTick, or contractSize
 * @returns Diagnostic object explaining resolution source and result
 */
export function getTickSizeResolutionDiagnostic(
  symbol?: string,
  assetClass?: string | null,
  instrument?: unknown,
): TickSizeResolutionDiagnostic {
  const originalSymbol = symbol || '(empty)';
  const inferredClass = inferAssetClass(symbol, assetClass);
  const root = extractFuturesRoot(symbol || '');
  const isFutures = inferredClass === 'futures';

  // Check instrument metadata first
  const fromInstrument = getInstrumentTickSize(instrument);
  if (fromInstrument != null && isFutures) {
    return {
      assetClass: 'futures',
      originalSymbol,
      normalizedSymbol: root || null,
      isFutures: true,
      resolutionSource: 'instrument-metadata',
      resolvedTickSize: fromInstrument,
      unresolvedReason: null,
    };
  }

  // Check fallback map
  if (isFutures && root) {
    const fromFallback = CME_FUTURES_TICK_SIZES[root];
    if (Number.isFinite(fromFallback) && fromFallback > 0) {
      return {
        assetClass: 'futures',
        originalSymbol,
        normalizedSymbol: root,
        isFutures: true,
        resolutionSource: 'fallback-root-map',
        resolvedTickSize: fromFallback,
        unresolvedReason: null,
      };
    }
  }

  // Could not resolve
  let reason: string | null = null;
  if (!symbol) {
    reason = 'empty-symbol';
  } else if (isFutures && !root) {
    reason = 'root-extraction-failed';
  } else if (isFutures && root && !CME_FUTURES_TICK_SIZES[root]) {
    reason = 'unknown-futures-root';
  } else if (!isFutures) {
    reason = 'not-classified-as-futures';
  } else {
    reason = 'unknown';
  }

  return {
    assetClass: inferredClass,
    originalSymbol,
    normalizedSymbol: root || null,
    isFutures,
    resolutionSource: 'unresolved',
    resolvedTickSize: null,
    unresolvedReason: reason,
  };
}

export function getContractMultiplier(
  symbol: string | undefined,
  assetClass?: string | null,
  instrument?: any
): number {
  if (!symbol) return 1;
  const s = String(symbol).toUpperCase().trim();

  // If instrument metadata is provided prefer it. Common metadata keys: `multiplier` or `contractSize`.
  if (instrument && typeof instrument === 'object') {
    if (typeof instrument.multiplier === 'number' && Number.isFinite(instrument.multiplier)) {
      return instrument.multiplier;
    }
    if (typeof instrument.contractSize === 'number' && Number.isFinite(instrument.contractSize)) {
      return instrument.contractSize;
    }
  }

  if ((assetClass || '').toUpperCase() === 'FUTURES') {
    const root = extractFuturesRoot(s);
    if (root && Object.prototype.hasOwnProperty.call(CME_FUTURES_MULTIPLIERS, root)) {
      return CME_FUTURES_MULTIPLIERS[root];
    }
    console.warn('[contractMultipliers] Unknown futures symbol root; defaulting multiplier=1', {
      symbol: s,
      root,
    });
    return 1;
  }

  // CASH (forex): registry supports per-symbol override when qty represents lots instead of base-units.
  if ((assetClass || '').toUpperCase() === 'CASH') {
    // Default: assume qty is base-currency units (multiplier = 1).
    // If your imported qty is in "lots", change this mapping to the contract size you use (e.g. 100000).
    const forexOverrides: Array<{ re: RegExp; multiplier: number }> = [
      // Example (comment/adjust as needed):
      // { re: /^EURUSD$|^EUR\/USD$/i, multiplier: 100000 },
    ];
    for (const m of forexOverrides) if (m.re.test(s)) return m.multiplier;
    return 1;
  }

  // Default for stocks / crypto / others
  return 1;
}
